import { booleanAttribute, computed, Directive, Input, input, signal } from '@angular/core';
import { sonaHlm } from '@sona/angular-util-common';
import { hlm } from '@spartan-ng/ui-core';
import { cva, type VariantProps } from 'class-variance-authority';

import type { ClassValue } from 'clsx';

export const badgeVariants = cva(
    'tw-inline-flex tw-items-center tw-whitespace-nowrap tw-border tw-rounded-full tw-no-underline tw-font-regular tw-transition-colors tw-leading-none focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2',
    {
        variants: {
            variant: {
                default: 'tw-bg-primary tw-border-transparent tw-text-primary-foreground',
                secondary: 'tw-bg-secondary tw-border-transparent tw-text-secondary-foreground',
                destructive: 'tw-bg-destructive tw-border-transparent tw-text-destructive-foreground',
                green: '',
                amber: '',
                orange: '',
                red: '',
                blue: 'tw-bg-blue-500 tw-border-transparent tw-text-white',
                outline: 'tw-text-foreground tw-border-border',
                'accent-amber': 'tw-bg-accent-amber-500 tw-text-accent-amber-950 tw-border-transparent',
                'accent-electric-blue': 'tw-bg-accent-electric-blue tw-text-accent-oxford-blue tw-border-transparent',
                'accent-green': 'tw-bg-accent-green tw-text-green-950 tw-border-transparent'
            },
            size: {
                default: 'tw-text-xs tw-px-1.5 tw-h-5',
                lg: 'tw-text-sm tw-px-2 tw-h-6',
                xl: 'tw-text-default tw-px-2.5 tw-h-8',
                responsive: 'tw-text-[0.375em] tw-px-1.5 tw-py-0.5',
                'responsive-lg': 'tw-text-[0.5em] tw-px-2.5 tw-py-1'
            },
            static: { true: '', false: '' }
        },
        compoundVariants: [
            {
                variant: 'default',
                static: false,
                class: 'hover:tw-bg-primary/80'
            },
            {
                variant: 'secondary',
                static: false,
                class: 'hover:tw-bg-secondary/80'
            },
            {
                variant: 'destructive',
                static: false,
                class: 'hover:tw-bg-destructive/80'
            },
            {
                variant: 'outline',
                static: false,
                class: 'hover:tw-bg-gray-200 hover:tw-border-gray-400'
            },
            {
                variant: 'green',
                static: false,
                class: 'hover:tw-bg-green-100 hover:tw-text-green-900 hover:tw-border-green-500'
            },
            {
                variant: 'amber',
                static: false,
                class: 'hover:tw-bg-amber-100 hover:tw-text-amber-900 hover:tw-border-amber-500'
            },
            {
                variant: 'orange',
                static: false,
                class: 'hover:tw-bg-orange-100 hover:tw-text-orange-900 hover:tw-border-orange-500'
            },
            {
                variant: 'red',
                static: false,
                class: 'hover:tw-bg-red-100 hover:tw-text-red-900 hover:tw-border-red-500'
            }
        ],
        defaultVariants: {
            variant: 'default',
            size: 'default',
            static: false
        }
    }
);
export type BadgeVariants = VariantProps<typeof badgeVariants>;

@Directive({
    selector: '[hlmBadge]',
    standalone: true,
    host: {
        '[class]': '_computedClass()'
    }
})
export class HlmBadgeDirective {
    public readonly userClass = input<ClassValue>('', { alias: 'class' });
    protected _computedClass = computed(() =>
        sonaHlm(badgeVariants({ variant: this._variant(), size: this._size(), static: this._static() }), this.userClass())
    );

    private readonly _variant = signal<BadgeVariants['variant']>('default');
    @Input()
    set variant(variant: BadgeVariants['variant']) {
        this._variant.set(variant);
    }

    private readonly _static = signal<BadgeVariants['static']>(false);
    @Input({ transform: booleanAttribute })
    set static(value: BadgeVariants['static']) {
        this._static.set(value);
    }

    private readonly _size = signal<BadgeVariants['size']>('default');
    @Input()
    set size(size: BadgeVariants['size']) {
        this._size.set(size);
    }
}
